import { useEffect, useState } from "react";
import AppShell, { Protected } from "@/components/AppShell";
import { api } from "@/lib/api";
import { normalizeText } from "@/lib/text";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, MagnifyingGlass, PencilSimple, Trash, IdentificationCard, Upload, ArrowClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import PatientForm from "@/components/PatientForm";
import CsvImportDialog from "@/components/CsvImportDialog";
import { Link } from "react-router-dom";
import { useAccessGate } from "@/hooks/useAccessGate";
import { useAuth } from "@/lib/auth";

function fmtDate(iso) {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

export default function PatientRecords() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [csvOpen, setCsvOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const { gateProps } = useAccessGate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const load = async ({ silent = false } = {}) => {
    if (!silent) setRefreshing(true);
    try {
      const { data } = await api.get("/patient-records");
      setItems(data);
    } catch (err) {
      if (!silent) toast.error("Impossible de rafraîchir la liste");
    } finally {
      if (!silent) setRefreshing(false);
    }
  };
  useEffect(() => { load({ silent: true }); }, []);

  const del = async (id) => {
    if (!confirm("Supprimer ce patient ?")) return;
    try { await api.delete(`/patient-records/${id}`); toast.success("Patient supprimé"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const filtered = items.filter((p) => {
    if (!q) return true;
    const s = normalizeText(q);
    return normalizeText(`${p.first_name} ${p.last_name}`).includes(s)
      || normalizeText(p.email || "").includes(s)
      ;
  });

  return (
    <Protected>
      <AppShell
        title="Patients"
        actions={
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => load()}
              disabled={refreshing}
              className="rounded-[10px] font-semibold group disabled:opacity-50 disabled:cursor-wait"
              data-testid="refresh-patients-btn"
              title="Rafraîchir"
            >
              <ArrowClockwise size={14} weight="bold" className={`mr-1.5 ${refreshing ? "animate-spin" : "transition-transform group-hover:rotate-45"}`} />
              Rafraîchir
            </Button>
            {isAdmin && (
              <Button variant="outline"
                className="rounded-[10px] border-[#3A8FA5] text-[#3A8FA5] hover:bg-[#3A8FA5] hover:text-white font-semibold disabled:opacity-40 disabled:cursor-not-allowed" data-testid="import-csv-btn"
                {...gateProps({ onClick: () => setCsvOpen(true), title: "Importer un CSV" })}>
                <Upload size={16} className="mr-2" /> Importer CSV
              </Button>
            )}
            <Button
              className="rounded-[10px] bg-[#3A8FA5] hover:bg-[#164E5F] text-white font-semibold disabled:opacity-40 disabled:cursor-not-allowed" data-testid="new-patient-btn"
              {...gateProps({ onClick: () => { setEditTarget(null); setFormOpen(true); }, title: "Nouveau patient" })}>
              <Plus size={16} className="mr-2" /> Nouveau patient
            </Button>
          </div>
        }
      >
        <div className="mb-6 relative max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5F758F]" />
          <Input placeholder="Rechercher un patient…" value={q} onChange={(e) => setQ(e.target.value)}
            className="pl-9 bg-white border-[#E1E9F0] rounded-[10px] focus-visible:ring-[#3A8FA5]" data-testid="patient-search" />
        </div>

        {filtered.length === 0 ? (
          <div className="bg-white border border-[#E1E9F0] rounded-2xl p-16 text-center">
            <IdentificationCard size={48} weight="duotone" className="text-[#3A8FA5] mx-auto mb-4" />
            <p className="text-2xl font-bold tracking-tight text-[#1E3A5F] mb-3">Aucun patient</p>
            <p className="text-[#5F758F] mb-6">Créez d'abord un patient pour pouvoir lui envoyer un questionnaire.</p>
            <Button
              className="rounded-[10px] bg-[#3A8FA5] hover:bg-[#164E5F] text-white font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
              {...gateProps({ onClick: () => { setEditTarget(null); setFormOpen(true); }, title: "Créer un patient" })}>
              <Plus size={16} className="mr-2" /> Créer le premier patient
            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-2.5">
            {filtered.map((p) => (
              <Link
                key={p.patient_id}
                to={`/patient-records/${p.patient_id}`}
                className="bg-white border border-[#E1E9F0] rounded-[14px] px-6 py-5 flex items-center justify-between gap-4 transition-all hover:border-[#3A8FA5] hover:shadow-[0_2px_8px_rgba(58,143,165,0.08)]"
                data-testid={`patient-${p.patient_id}`}
              >
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <div className="w-11 h-11 rounded-[10px] bg-[#3A8FA5] text-white flex items-center justify-center font-bold text-sm shrink-0">
                    {`${(p.first_name || "?").charAt(0)}${(p.last_name || "").charAt(0)}`.toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="text-[16px] font-semibold text-[#1E3A5F] truncate">{p.first_name} {p.last_name}</p>
                    <p className="text-[14px] text-[#5F758F] truncate mt-0.5">
                      Né(e) le {fmtDate(p.date_of_birth)}{p.email ? ` · ${p.email}` : ""}
                    </p>
                  </div>
                </div>
                <div className="flex gap-1 shrink-0" onClick={(e) => e.preventDefault()}>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="rounded-[10px] text-[#5F758F] hover:text-[#3A8FA5] hover:bg-[#E6F1F5] disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid={`edit-${p.patient_id}`}
                    {...gateProps({
                      onClick: (e) => { e.preventDefault(); e.stopPropagation(); setEditTarget(p); setFormOpen(true); },
                      title: "Modifier le patient",
                    })}
                  ><PencilSimple size={16} /></Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="rounded-[10px] text-[#5F758F] hover:text-[#D45A3F] hover:bg-[#FFE0DC] disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid={`del-${p.patient_id}`}
                    {...gateProps({
                      onClick: (e) => { e.preventDefault(); e.stopPropagation(); del(p.patient_id); },
                      title: "Supprimer le patient",
                    })}
                  ><Trash size={16} /></Button>
                </div>
              </Link>
            ))}
          </div>
        )}

        <PatientForm
          open={formOpen}
          initial={editTarget}
          onClose={() => { setFormOpen(false); setEditTarget(null); }}
          onSaved={() => { setFormOpen(false); setEditTarget(null); load(); }}
        />
        <CsvImportDialog
          open={csvOpen}
          onClose={() => setCsvOpen(false)}
          onImported={() => load()}
        />
      </AppShell>
    </Protected>
  );
}
